import { useMemo, useState } from 'react';
import ProductCard from '@/components/feature/ProductCard';
import { getConcernsByCategory, getProductsByCategory } from '@/pages/products/productUtils';

interface ProductBrowserProps {
  categoryId: string;
  categoryName: string;
}

export default function ProductBrowser({ categoryId, categoryName }: ProductBrowserProps) {
  const [query, setQuery] = useState('');
  const [concern, setConcern] = useState('All');

  const allProducts = useMemo(() => getProductsByCategory(categoryId), [categoryId]);
  const concerns = useMemo(() => getConcernsByCategory(categoryId), [categoryId]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return allProducts.filter((product) => {
      const matchesConcern = concern === 'All' || product.concern === concern;
      const matchesTerm =
        !term ||
        product.name.toLowerCase().includes(term) ||
        product.concern.toLowerCase().includes(term) ||
        product.description.toLowerCase().includes(term);
      return matchesConcern && matchesTerm;
    });
  }, [allProducts, concern, query]);

  return (
    <section className="w-full bg-background-50 py-12 md:py-16">
      <div className="w-full px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
              {categoryName} Range
            </h2>
            <p className="mt-1 text-sm text-foreground-500">
              {filtered.length} {filtered.length === 1 ? 'product' : 'products'} available
            </p>
          </div>

          <div className="relative w-full lg:w-80">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Search ${categoryName.toLowerCase()}...`}
              aria-label={`Search ${categoryName} products`}
              className="w-full pl-10 pr-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
            />
          </div>
        </div>

        {concerns.length > 1 ? (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {['All', ...concerns].map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setConcern(item)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  concern === item
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900'
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        ) : null}

        {filtered.length > 0 ? (
          <div
            data-product-shop="true"
            className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
          >
            {filtered.map((product) => (
              <ProductCard key={product.id} product={product} categoryLabel={categoryName} />
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-lg border border-background-200 bg-background-100/70 p-10 text-center">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-50 text-foreground-500">
              <i className="ri-search-line text-2xl"></i>
            </span>
            <h3 className="font-heading mt-4 text-lg font-semibold text-foreground-950">No products found</h3>
            <p className="mt-2 text-sm text-foreground-600">
              Try a different search term or clear the filters to see the full range.
            </p>
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setConcern('All');
              }}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line text-base"></i>
              Reset Filters
            </button>
          </div>
        )}
      </div>
    </section>
  );
}